import type { ReactNode } from 'react';

/* ---------------------------------------------------------------------------
 * Every icon in the app, in one file - the studio and the guest page both
 * draw from it.
 *
 * Inline rather than from a library: the project deliberately carries no icon
 * dependency, and pulling one in for a dozen glyphs is a decision of its own
 * rather than a detail of this panel or that one. Keeping them together is
 * what stops the next one being redrawn slightly differently three files away.
 *
 * They are decorative throughout - `aria-hidden`, never the only thing a
 * control says. Menu entries keep their text label, and an icon-only button
 * keeps its `title`, so nothing here is load-bearing for a screen reader.
 *
 * Icons that describe a state (a microphone, a camera, an eye) take an `on`
 * flag and draw the struck-through variant themselves, so a button never has
 * to choose between two components to say which state it is in.
 * ------------------------------------------------------------------------ */

/**
 * The shell every glyph shares: a 24-unit grid drawn at 16px, so the shapes
 * below are all authored against the same coordinates and line weights.
 */
function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

/** The diagonal that turns any icon into its "not doing that" variant. */
export function Strike() {
  return <line x1="3" y1="21" x2="21" y2="3" stroke="currentColor" strokeWidth="2" />;
}

export function MicIcon({ on }: { on: boolean }) {
  return (
    <Glyph>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {on ? null : <Strike />}
    </Glyph>
  );
}

/**
 * A camcorder. It doubles as the icon for a video layer, where there is no
 * off state to draw - hence the default.
 */
export function CameraIcon({ on = true }: { on?: boolean }) {
  return (
    <Glyph>
      <rect x="2" y="6" width="13" height="12" rx="2" fill="currentColor" />
      <path d="M15 11l7-4v10l-7-4z" fill="currentColor" />
      {on ? null : <Strike />}
    </Glyph>
  );
}

export function PlusIcon() {
  return (
    <Glyph>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Glyph>
  );
}

export function CloseIcon() {
  return (
    <Glyph>
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Glyph>
  );
}

/**
 * Deleting a scene or a layer gets a bin rather than a cross: a cross reads as
 * "close this", and the two sit next to each other in the same row of actions.
 */
export function TrashIcon() {
  return (
    <Glyph>
      <path
        d="M4 7h16M10 4h4M6 7l1 13h10l1-13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </Glyph>
  );
}

export function PencilIcon() {
  return (
    <Glyph>
      <path
        d="M4 20h4L20 8a2.83 2.83 0 0 0-4-4L4 16v4z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14.5 5.5l4 4" stroke="currentColor" strokeWidth="2" />
    </Glyph>
  );
}

export function DuplicateIcon() {
  return (
    <Glyph>
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M15 4H6a2 2 0 0 0-2 2v9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

export function EyeIcon({ on }: { on: boolean }) {
  return (
    <Glyph>
      <path
        d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      {on ? null : <Strike />}
    </Glyph>
  );
}

/**
 * The shackle, not the body, carries the state: a padlock whose only
 * difference is a struck-through line is unreadable at 16px, whereas an open
 * hoop is obvious at a glance.
 */
export function LockIcon({ locked }: { locked: boolean }) {
  return (
    <Glyph>
      <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d={locked ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 8 0'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

/**
 * Properties gets sliders rather than a gear. A gear reads as "settings for
 * the application"; this button opens the values of one particular layer.
 */
export function SlidersIcon() {
  return (
    <Glyph>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="9" cy="7" r="2.5" fill="currentColor" />
      <circle cx="15" cy="12" r="2.5" fill="currentColor" />
      <circle cx="7" cy="17" r="2.5" fill="currentColor" />
    </Glyph>
  );
}

export function ScreenShareIcon() {
  return (
    <Glyph>
      <rect x="2" y="4" width="20" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 21h8M12 13.5V8m0 0l-2.5 2.5M12 8l2.5 2.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

/**
 * Detaching the programme into a window of its own. An arrow leaving a frame
 * rather than a second monitor: what it opens is this composition somewhere
 * else, not a different screen.
 */
export function DetachIcon() {
  return (
    <Glyph>
      <path
        d="M13 4h7v7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M20 4l-8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

/** The auto layer, which lays the participants out for you. */
export function PeopleIcon() {
  return (
    <Glyph>
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="2" />
      <path
        d="M3 19a6 6 0 0 1 12 0M16.4 5.6a3.2 3.2 0 0 1 0 6.3M17 14.4a5.6 5.6 0 0 1 4 4.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

export function ImageIcon() {
  return (
    <Glyph>
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
      <circle cx="9" cy="9.5" r="1.8" fill="currentColor" />
      <path
        d="M4 17l4.5-4.5 4 4L16 13l4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

export function TextIcon() {
  return (
    <Glyph>
      <path
        d="M5 6V4h14v2M12 4v16M9 20h6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

export function ShapeIcon() {
  return (
    <Glyph>
      <rect x="3" y="3" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="2" />
      <circle cx="15.5" cy="15.5" r="5.5" stroke="currentColor" strokeWidth="2" />
    </Glyph>
  );
}

/** A page divided into regions, which is what a template is before it is filled. */
export function TemplateIcon() {
  return (
    <Glyph>
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 9.5h18M11 9.5V20" stroke="currentColor" strokeWidth="2" />
    </Glyph>
  );
}
